import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { organizationAtom } from 'services/organization/atom';
import {
  OrganizationGroupRole,
  useGetUsersForOrganizationQuery,
  useGetUsersByOrganizationGroupQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { Option } from 'common/types';

const mergeUserOptions = (
  newOptions: Option[],
  existingOptions: Option[]
): Option[] => {
  const existingOptionsMap = new Map(
    existingOptions.map((option) => [option.value, option])
  );

  newOptions.forEach((option) => {
    if (!existingOptionsMap.has(option.value)) {
      existingOptionsMap.set(option.value, option);
    }
  });

  const combinedOptions = Array.from(existingOptionsMap.values());
  combinedOptions.sort((a, b) => {
    if (a.label && b.label) {
      return a.label.localeCompare(b.label);
    }
    return 0;
  });

  return combinedOptions;
};

export const useUserOrganizationOptions = () => {
  const organization = useRecoilValue(organizationAtom);
  const [userOrganizationOptions, setUserOrganizationOptions] = useState<
    Option[]
  >([]);

  const { loading: userOrganizationIsLoading } =
    useGetUsersForOrganizationQuery({
      skip: !organization?.id,
      variables: {
        organizationId: organization?.id || ''
      },
      onCompleted: (data) => {
        const users = data?.getUsersForOrganization?.items || [];
        const activeUsers = users.filter((user) => user.isOnboard);

        const newUserOptions = activeUsers.map((user) => ({
          label: user.name?.trim() ?? user.email?.trim() ?? '',
          value: user.id
        }));

        setUserOrganizationOptions((prevOptions) =>
          mergeUserOptions(newUserOptions, prevOptions)
        );
      }
    });

  const { loading: userGroupAdminsIsLoading } =
    useGetUsersByOrganizationGroupQuery({
      variables: {
        organizationGroupId: organization?.organizationGroupId || '',
        filter: {
          organizationGroupRole: {
            eq: OrganizationGroupRole.OrganizationGroupAdmin
          }
        }
      },
      skip: !organization?.organizationGroupId,
      onCompleted: (data) => {
        const result = extractQueryListItems(data);
        const activeUserOrganizations = result.filter((item) => item.isOnboard);

        const newAdminOptions = activeUserOrganizations.map((item) => ({
          label: item.name?.trim() ?? item.email.trim(),
          value: item.id
        }));

        setUserOrganizationOptions((prevOptions) =>
          mergeUserOptions(newAdminOptions, prevOptions)
        );
      }
    });

  return {
    userOrganizationOptions,
    isLoading: userOrganizationIsLoading || userGroupAdminsIsLoading
  };
};
